import styled, { css } from "styled-components";
import { createRef } from "react";
import { NavLink } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";

import HomeProduct from "./components/HomeProduct";
import HomeFooter from "./components/HomeFooter";
import HomeContact from "./components/HomeContact";
import { ReactComponent as Logo } from "@/assets/icons/logo.svg";
import Text from "@/components/Text";
import HeroImage from "@/assets/images-for-home/hero.png";

export const Wrap = styled.div`
  background: radial-gradient(
    167.27% 117.27% at 50% 50%,
    rgba(132, 84, 214, 0.15) 0%,
    rgba(255, 213, 111, 0.15) 100%
  );
  backdrop-filter: blur(150px);
`;

const HeroHolder = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  padding: 100px 0;
`;

export const Header = styled.header`
  padding: 12px 40px;
  display: flex;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
`;

const StyledLogo = styled(Logo)`
  height: 20px;
  width: auto;
`;

export const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
`;

export const StyledNavLink = styled(NavLink)`
  padding: 0 24px;
  margin-left: 64px;
  height: 54px;
  justify-content: center;
  transition: 0.2s ease-out all;
  display: inline-flex;
  align-items: center;
  padding: 0 40px;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.button.fontWeight};
    font-size: ${theme.typography.button.fontSize};
    line-height: ${theme.typography.button.lineHeight};
    color: ${theme.colors.purple};
    border-color: ${theme.colors.purple};
    &:hover {
      color: ${theme.colors.white};
      border-color: ${theme.colors.purpleLighter};
      background: ${theme.colors.purpleLighter};
    }
  `}
`;

export const Link = styled(Text)`
  margin-left: 40px;
  cursor: pointer;
  transition: opacity ease-out 0.2s;
  &:hover {
    opacity: 0.75;
  }
`;

const HeroLeft = styled.div`
  width: 350px;
  max-width: 45%;
`;

const HeadingSpan = styled.span`
  color: ${({ theme }) => theme.colors.purple};
`;

const Heading = styled(Text)`
  margin-bottom: 32px;
`;

const Description = styled(Text)`
  line-height: 22px;
  margin-bottom: 104px;
`;

const More = styled(Text)`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  svg {
    margin-left: 16px;
  }
  transition: opacity ease-out 0.2s;
  &:hover {
    opacity: 0.5;
  }
`;

const Image = styled(LazyLoadImage)`
  display: block;
  position: absolute;
  width: 60%;
  right: 0;
  object-fit: contain;
  object-position: right;
  max-height: calc(100vh - 70px);
  top: 60px;
  bottom: 0;
  margin: auto;
`;

export const HomeContainer = styled.div`
  padding: 0 20px;
  width: 100%;
  max-width: 1128px;
  margin: 0 auto;
`;

const Home = () => {
  const productRef = createRef<null | HTMLDivElement>();

  return (
    <Wrap>
      <HeroHolder>
        <Header>
          <NavLink to="/">
            <StyledLogo />
          </NavLink>
          <Nav>
            <Link
              $type="caption"
              onClick={() =>
                productRef.current?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Product
            </Link>
            <StyledNavLink to="/login">Sign Up</StyledNavLink>
          </Nav>
        </Header>
        <HomeContainer>
          <HeroLeft>
            <Heading $type="heading1" as="h1">
              Complex Integration Made <HeadingSpan>Super Easy</HeadingSpan>
            </Heading>
            <Description $type="captionSemibold" $color="gray">
              Track data through multiple channels and networks to get a clear
              data-driven view of market performance. Backbone helps you to stay
              in the loop with your niche by reporting content performance
              metrics and automating manual or gut-feeling tasks
            </Description>
            <More
              $type="button"
              $color="purple"
              onClick={() =>
                productRef.current?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Find out more
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.9998 23.0768C18.1174 23.0768 23.0767 18.1175 23.0767 11.9999C23.0767 5.88228 18.1174 0.922974 11.9998 0.922974C5.88216 0.922974 0.922852 5.88228 0.922852 11.9999C0.922852 18.1175 5.88216 23.0768 11.9998 23.0768Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M13 7.38452C13 6.83224 12.5523 6.38452 12 6.38452C11.4477 6.38452 11 6.83224 11 7.38452V14.2018L8.79011 11.9919C8.39959 11.6013 7.76643 11.6013 7.3759 11.9919C6.98538 12.3824 6.98538 13.0156 7.3759 13.4061L11.2922 17.3224C11.3803 17.4104 11.4831 17.4804 11.5948 17.5298C11.7187 17.5848 11.8558 17.6153 12 17.6153C12.288 17.6153 12.5477 17.4935 12.7301 17.2986L16.6227 13.4061C17.0132 13.0156 17.0132 12.3824 16.6227 11.9919C16.2321 11.6013 15.599 11.6013 15.2084 11.9919L13 14.2003V7.38452Z"
                  fill="currentColor"
                />
              </svg>
            </More>
          </HeroLeft>
        </HomeContainer>
        <Image src={HeroImage} alt="Complex Integration Made Super Easy" />
      </HeroHolder>
      <HomeProduct ref={productRef} />
      <HomeContact />
      <HomeFooter />
    </Wrap>
  );
};

export default Home;
